import { useEffect, useMemo, useState } from "react";

export type Block = any;

const BANNED_TAGS = /<\/?(?:script|iframe|object|embed|style|link|meta|base|form|input|button|textarea|select|svg|math|frame|frameset)\b[^>]*>/gi;

const sanitizeHtml = (html: string): string => {
  if (!html) return "";
  let s = String(html);
  s = s.replace(/<!--[\s\S]*?-->/g, "");
  s = s.replace(/<script[\s\S]*?<\/script>/gi, "");
  s = s.replace(/<style[\s\S]*?<\/style>/gi, "");
  s = s.replace(BANNED_TAGS, "");
  s = s.replace(/\son\w+\s*=\s*"[^"]*"/gi, "");
  s = s.replace(/\son\w+\s*=\s*'[^']*'/gi, "");
  s = s.replace(/\son\w+\s*=\s*[^\s>]+/gi, "");
  s = s.replace(/(href|src|xlink:href|formaction|action|srcset|data|poster|background)\s*=\s*(["'])\s*(?:javascript|data|vbscript|file):[^"']*\2/gi, '$1=$2#$2');
  s = s.replace(/(href|src|xlink:href|formaction|action|srcset|data|poster|background)\s*=\s*(?:javascript|data|vbscript|file):[^\s>]*/gi, '$1=#');
  return s;
};

const safeHref = (href?: string): string => {
  if (!href) return "#";
  const t = String(href).trim();
  if (/^(https?:|mailto:|tel:|\/|#|\.\/|\.\.\/)/i.test(t)) return t;
  return "#";
};

const alignClass = (a?: string) => a === "center" ? "text-center" : a === "right" ? "text-right" : "text-left";

function YouTubeEmbed({ url }: { url: string }) {
  const id = useMemo(() => {
    const m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|v\/))([\w-]{6,})/);
    return m ? m[1] : null;
  }, [url]);
  if (!id) return <video src={url} controls className="w-full rounded-xl" />;
  return (
    <div className="relative w-full" style={{ paddingBottom: "56.25%" }}>
      <iframe className="absolute inset-0 w-full h-full rounded-xl" src={`https://www.youtube.com/embed/${id}`} title="Video" frameBorder={0} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
    </div>
  );
}

function FaqBlock({ items }: { items: { question: string; answer: string }[] }) {
  const [open, setOpen] = useState<number | null>(0);
  return (
    <div className="divide-y divide-slate-200 border border-slate-200 rounded-2xl bg-white">
      {(items || []).map((it, i) => (
        <div key={i}>
          <button onClick={() => setOpen(open === i ? null : i)} className="w-full px-5 py-4 text-left flex items-center justify-between gap-4 hover:bg-slate-50">
            <span className="font-semibold text-slate-900">{it.question}</span>
            <span className="text-primary text-xl">{open === i ? "−" : "+"}</span>
          </button>
          {open === i && <div className="px-5 pb-4 text-slate-700 leading-relaxed" dangerouslySetInnerHTML={{ __html: sanitizeHtml(it.answer) }} />}
        </div>
      ))}
    </div>
  );
}

function RelatedBlock({ block, ctx }: { block: any; ctx: Record<string, any> }) {
  const [links, setLinks] = useState<{ title: string; href: string }[]>([]);
  useEffect(() => {
    if (!block.collection || !block.pattern) return;
    const qs = new URLSearchParams({ collection: block.collection, pattern: block.pattern, limit: String(block.limit || 12), context: JSON.stringify(ctx || {}) });
    fetch(`${(import.meta as any).env.BASE_URL}api/internal-links?${qs}`).then(r => r.json()).then(setLinks).catch(() => setLinks([]));
  }, [block.collection, block.pattern, block.limit]);
  if (!links.length) return null;
  const cols = Math.max(1, Math.min(6, block.columns || 3));
  return (
    <div className="bg-slate-50 rounded-2xl p-6">
      {block.label && <h3 className="text-xl font-bold text-slate-900 mb-4">{block.label}</h3>}
      <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}>
        {links.map((l, i) => (
          <a key={i} href={safeHref(l.href)} className="px-3 py-2 rounded-lg bg-white hover:bg-primary hover:text-white text-slate-700 text-sm font-medium transition border border-slate-200">{l.title}</a>
        ))}
      </div>
    </div>
  );
}

export function PageRenderer({ blocks, context }: { blocks: Block[]; context?: Record<string, any> }) {
  if (!Array.isArray(blocks) || blocks.length === 0) {
    return <div className="text-center text-slate-400 py-20">No content yet.</div>;
  }
  const ctx = context || {};
  return (
    <div className="space-y-6">
      {blocks.map((b: Block, i: number) => {
        switch (b.type) {
          case "heading": {
            const lvl = Math.min(4, Math.max(1, b.level || 2));
            const sizes = ["", "text-4xl md:text-5xl", "text-3xl md:text-4xl", "text-2xl md:text-3xl", "text-xl md:text-2xl"];
            const style: any = {};
            if (b.fontFamily) style.fontFamily = b.fontFamily;
            if (b.fontSize) style.fontSize = `${b.fontSize}px`;
            if (b.color) style.color = b.color;
            const Tag = (`h${lvl}`) as any;
            return <Tag key={i} className={`${sizes[lvl]} font-bold ${alignClass(b.align)} text-slate-900`} style={style}>{typeof b.linkedText === "string" ? <span dangerouslySetInnerHTML={{ __html: sanitizeHtml(b.linkedText) }} /> : b.text}</Tag>;
          }
          case "paragraph": {
            const style: any = {};
            if (b.fontFamily) style.fontFamily = b.fontFamily;
            if (b.fontSize) style.fontSize = `${b.fontSize}px`;
            if (b.color) style.color = b.color;
            return <div key={i} className={`text-base leading-relaxed text-slate-700 ${alignClass(b.align)}`} style={style} dangerouslySetInnerHTML={{ __html: sanitizeHtml(b.html || b.text || "") }} />;
          }
          case "image": {
            const w = b.width ? `${b.width}px` : "100%";
            const wrap = alignClass(b.align);
            const img = <img src={safeHref(b.url)} alt={b.alt || ""} style={{ maxWidth: "100%", width: w, height: "auto" }} className="rounded-xl inline-block" loading="lazy" decoding="async" />;
            return <div key={i} className={wrap}>{b.href ? <a href={safeHref(b.href)} rel="noopener">{img}</a> : img}</div>;
          }
          case "button": {
            const cls = b.style === "outline"
              ? "border-2 border-primary text-primary hover:bg-primary hover:text-white"
              : b.style === "secondary"
              ? "bg-slate-800 text-white hover:bg-slate-900"
              : "bg-primary text-white hover:bg-primary/90";
            return <div key={i} className={alignClass(b.align)}><a href={safeHref(b.href)} rel="noopener" className={`inline-block px-7 py-3 rounded-xl font-semibold transition ${cls}`}>{b.label}</a></div>;
          }
          case "divider":
            return <hr key={i} className="border-slate-200" />;
          case "spacer":
            return <div key={i} style={{ height: `${Math.max(4, Math.min(200, b.height || 24))}px` }} />;
          case "hero": {
            const overlay = Math.max(0, Math.min(0.9, b.overlay ?? 0.45));
            return (
              <div key={i} className="relative rounded-2xl overflow-hidden min-h-[320px] md:min-h-[440px] flex items-center" style={{
                backgroundImage: b.backgroundUrl ? `url(${b.backgroundUrl})` : undefined,
                backgroundSize: "cover", backgroundPosition: "center", backgroundColor: "#0F2F4F",
              }}>
                {b.backgroundUrl && <div className="absolute inset-0" style={{ background: `rgba(15,47,79,${overlay})` }} />}
                <div className={`relative z-10 px-8 md:px-16 py-16 w-full ${alignClass(b.align || "center")}`}>
                  <h1 className="text-3xl md:text-5xl font-bold text-white mb-4">{b.title}</h1>
                  {b.subtitle && <p className="text-lg md:text-xl text-white/90 mb-6 max-w-2xl mx-auto">{b.subtitle}</p>}
                  {b.ctaLabel && b.ctaHref && <a href={safeHref(b.ctaHref)} rel="noopener" className="inline-block bg-white text-primary px-7 py-3 rounded-xl font-semibold hover:bg-slate-100 transition">{b.ctaLabel}</a>}
                </div>
              </div>
            );
          }
          case "twoColumn": {
            const colCls = (v?: string, h?: string) => {
              const jc = v === "center" ? "justify-center" : v === "bottom" ? "justify-end" : "justify-start";
              const ta = h === "center" ? "text-center" : h === "right" ? "text-right" : "text-left";
              return `flex flex-col space-y-4 ${jc} ${ta}`;
            };
            const bg = b.background || "none";
            const isDarkBg = bg === "dark" || bg === "primary" || bg === "gradient";
            const wrapperBg = bg === "dark" ? "bg-slate-900 text-white"
              : bg === "primary" ? "bg-primary text-white"
              : bg === "light" ? "bg-slate-50 text-slate-900"
              : bg === "gradient" ? "bg-gradient-to-br from-primary via-primary to-blue-700 text-white"
              : "";
            // When the section has a dark/colored background, invert the hardcoded
            // text/bg colors that nested heading/paragraph/button blocks ship with,
            // so they're readable instead of dark-on-dark. Uses Tailwind arbitrary
            // descendant selectors targeting the literal class names emitted by the
            // child block renderers.
            const darkOverrides = isDarkBg
              ? "[&_.text-slate-900]:!text-white [&_.text-slate-700]:!text-white/90 [&_.text-slate-600]:!text-white/80 [&_.text-slate-500]:!text-white/70 [&_.bg-slate-800]:!bg-white [&_.bg-slate-800]:!text-slate-900 [&_.hover\\:bg-slate-900:hover]:!bg-slate-100"
              : "";
            const proseColor = isDarkBg ? "text-white/90 [&_a]:text-white [&_a]:underline [&_h1]:text-white [&_h2]:text-white [&_h3]:text-white [&_strong]:text-white" : "text-slate-700";
            const fullWidth = !!b.fullWidth;
            // Per-section spacing overrides. Vertical padding goes on the outer
            // wrapper (so the colored background gets taller). Horizontal padding
            // goes on the INNER content area so it actually narrows the content
            // (otherwise max-w-7xl always wins and paddingX appears to do nothing
            // on wide viewports).
            const hasCustomPy = typeof b.paddingY === "number";
            const hasCustomPx = typeof b.paddingX === "number";
            const roundedCls = bg !== "none" && !fullWidth ? "rounded-2xl" : "";
            const defaultPyCls = bg !== "none" && !hasCustomPy ? (fullWidth ? "py-14 md:py-20" : "py-10 md:py-14") : "";
            const defaultPxCls = bg !== "none" && !hasCustomPx ? "px-6 md:px-10" : "";
            // Escape the parent's max-w container by using w-screen + symmetric
            // negative margins. Works inside any centered wrapper.
            const escapeClass = fullWidth ? "relative left-1/2 right-1/2 -ml-[50vw] -mr-[50vw] w-screen" : "";
            const outerStyle: React.CSSProperties = {
              ...(b.backgroundUrl ? { backgroundImage: `url(${b.backgroundUrl})`, backgroundSize: "cover", backgroundPosition: "center" } : {}),
              ...(hasCustomPy ? { paddingTop: `${b.paddingY}px`, paddingBottom: `${b.paddingY}px` } : {}),
            };
            const hasLeft = !!(b.leftHtml || (Array.isArray(b.leftBlocks) && b.leftBlocks.length > 0));
            const hasRight = !!(b.rightHtml || (Array.isArray(b.rightBlocks) && b.rightBlocks.length > 0));
            // Collapse to a single column when one side is empty so nested
            // twoColumn-as-container patterns don't squish their content to half-width.
            const gridCols = hasLeft && hasRight ? "grid-cols-1 md:grid-cols-2" : "grid-cols-1";
            // When full-width, constrain the inner grid back to the article
            // width so text doesn't stretch edge-to-edge on wide screens.
            const innerConstrain = fullWidth ? "max-w-7xl mx-auto" : "";
            const innerStyle: React.CSSProperties = {
              gap: `${b.gap ?? 32}px`,
              ...(hasCustomPx ? { paddingLeft: `${b.paddingX}px`, paddingRight: `${b.paddingX}px` } : {}),
            };
            return (
              <div key={i} className={`relative overflow-hidden ${wrapperBg} ${defaultPyCls} ${roundedCls} ${darkOverrides} ${escapeClass}`} style={outerStyle}>
                {b.backgroundUrl && bg !== "none" && bg !== "light" && <div className="absolute inset-0 bg-slate-900/55" />}
                <div className={`relative z-10 grid ${gridCols} ${innerConstrain} ${defaultPxCls}`} style={innerStyle}>
                  {hasLeft && (
                    <div className={colCls(b.leftVAlign, b.leftAlign)}>
                      {b.leftHtml && <div className={`prose max-w-none ${proseColor}`} dangerouslySetInnerHTML={{ __html: sanitizeHtml(b.leftHtml) }} />}
                      {Array.isArray(b.leftBlocks) && b.leftBlocks.length > 0 && <PageRenderer blocks={b.leftBlocks} context={ctx} />}
                    </div>
                  )}
                  {hasRight && (
                    <div className={colCls(b.rightVAlign, b.rightAlign)}>
                      {b.rightHtml && <div className={`prose max-w-none ${proseColor}`} dangerouslySetInnerHTML={{ __html: sanitizeHtml(b.rightHtml) }} />}
                      {Array.isArray(b.rightBlocks) && b.rightBlocks.length > 0 && <PageRenderer blocks={b.rightBlocks} context={ctx} />}
                    </div>
                  )}
                  {/* Preserve empty editor slots when both sides are empty so the block isn't invisible */}
                  {!hasLeft && !hasRight && <div className="text-sm text-slate-400 italic">Empty two-column block</div>}
                </div>
              </div>
            );
          }
          case "columns": {
            const c = Math.max(2, Math.min(6, Number(b.columns) || 3));
            const items: any[] = Array.isArray(b.items) ? b.items : [];
            const colCls = (v?: string, h?: string) => {
              const jc = v === "center" ? "justify-center" : v === "bottom" ? "justify-end" : "justify-start";
              const ta = h === "center" ? "text-center" : h === "right" ? "text-right" : "text-left";
              return `flex flex-col space-y-4 ${jc} ${ta}`;
            };
            return (
              <div key={i} className="grid grid-cols-1" style={{ gap: `${b.gap ?? 24}px`, gridTemplateColumns: `repeat(${c}, minmax(0, 1fr))` }}>
                {items.slice(0, c).map((it, j) => (
                  <div key={j} className={colCls(it?.vAlign, it?.align)}>
                    {it?.html && <div className="prose max-w-none text-slate-700" dangerouslySetInnerHTML={{ __html: sanitizeHtml(it.html) }} />}
                    {Array.isArray(it?.blocks) && it.blocks.length > 0 && <PageRenderer blocks={it.blocks} context={ctx} />}
                  </div>
                ))}
              </div>
            );
          }
          case "video":
            return (
              <div key={i}>
                <YouTubeEmbed url={b.url} />
                {b.caption && <p className="text-sm text-slate-500 mt-2 text-center">{b.caption}</p>}
              </div>
            );
          case "html":
            return <div key={i} dangerouslySetInnerHTML={{ __html: sanitizeHtml(b.html || "") }} />;
          case "faq":
            return <FaqBlock key={i} items={b.items || []} />;
          case "related":
            return <RelatedBlock key={i} block={b} ctx={ctx} />;
          case "localBusiness":
            return (
              <div key={i} className="bg-white border border-slate-200 rounded-2xl p-6 grid md:grid-cols-[auto_1fr] gap-5 items-start">
                {b.image && <img src={safeHref(b.image)} alt={b.name} className="w-32 h-32 object-cover rounded-xl" />}
                <div className="space-y-1">
                  <h3 className="text-xl font-bold text-slate-900">{b.name}</h3>
                  {b.description && <p className="text-slate-600">{b.description}</p>}
                  <div className="mt-3 grid sm:grid-cols-2 gap-2 text-sm text-slate-700">
                    {b.telephone && <a href={`tel:${b.telephone}`} className="hover:text-primary">📞 {b.telephone}</a>}
                    {b.email && <a href={`mailto:${b.email}`} className="hover:text-primary">✉ {b.email}</a>}
                    {b.address && <span>📍 {[b.address.streetAddress, b.address.addressLocality, b.address.postalCode].filter(Boolean).join(", ")}</span>}
                    {b.openingHours && <span>🕒 {Array.isArray(b.openingHours) ? b.openingHours.join(" · ") : b.openingHours}</span>}
                  </div>
                </div>
              </div>
            );
          case "breadcrumb": {
            const items = b.items || [];
            return (
              <nav key={i} className="text-sm text-slate-500" aria-label="Breadcrumb">
                {items.map((c: any, j: number) => (
                  <span key={j}>
                    {j > 0 && <span className="mx-2">›</span>}
                    {j < items.length - 1 ? <a href={safeHref(c.url)} className="hover:text-primary">{c.name}</a> : <span className="text-slate-900 font-medium">{c.name}</span>}
                  </span>
                ))}
              </nav>
            );
          }
          case "gallery": {
            const cols = Math.max(1, Math.min(6, b.columns || 3));
            return (
              <div key={i} className={`grid gap-3`} style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}>
                {(b.images || []).map((img: any, j: number) => (
                  <img key={j} src={img.url} alt={img.alt || ""} className="w-full h-full object-cover rounded-lg aspect-square" loading="lazy" />
                ))}
              </div>
            );
          }
          case "list": {
            const style = b.style || (b.ordered ? "number" : "bullet");
            const cols = Math.max(1, Math.min(3, Number(b.columns) || 1));
            const items: string[] = b.items || [];
            const itemContent = (text: string, index: number) => typeof b.linkedItems?.[index] === "string"
              ? <span dangerouslySetInnerHTML={{ __html: sanitizeHtml(b.linkedItems[index]) }} />
              : text;
            const colStyle = cols > 1 ? { columnCount: cols, columnGap: "2rem" } : undefined;
            if (style === "check") {
              return (
                <ul key={i} className="space-y-2.5" style={colStyle}>
                  {items.map((it, j) => (
                    <li key={j} className="flex items-start gap-2.5 break-inside-avoid">
                      <span className="mt-0.5 inline-flex items-center justify-center w-5 h-5 rounded-full border-2 border-current shrink-0 text-[11px] leading-none font-bold">✓</span>
                      <span className="flex-1">{itemContent(it, j)}</span>
                    </li>
                  ))}
                </ul>
              );
            }
            const Tag = (style === "number" ? "ol" : "ul") as any;
            return <Tag key={i} className={`${style === "number" ? "list-decimal" : "list-disc"} pl-6 space-y-1`} style={colStyle}>{items.map((it, j) => <li key={j} className="break-inside-avoid">{itemContent(it, j)}</li>)}</Tag>;
          }
          case "quote":
            return (
              <blockquote key={i} className="border-l-4 border-primary pl-6 py-2 italic text-slate-600">
                <p className="text-lg">"{b.text}"</p>
                {b.author && <footer className="mt-2 text-sm text-slate-500 not-italic">— {b.author}</footer>}
              </blockquote>
            );
          case "cta": {
            const bg = b.background || "primary";
            const bgClass = bg === "dark" ? "bg-slate-900 text-white"
              : bg === "light" ? "bg-slate-100 text-slate-900"
              : bg === "gradient" ? "bg-gradient-to-br from-primary via-primary to-blue-600 text-white"
              : "bg-primary text-white";
            const subColor = bg === "light" ? "text-slate-600" : "text-white/85";
            const primaryBtn = bg === "light" ? "bg-primary text-white hover:bg-primary/90" : "bg-white text-primary hover:bg-slate-100";
            const secondaryBtn = bg === "light" ? "border-2 border-primary text-primary hover:bg-primary hover:text-white" : "border-2 border-white/70 text-white hover:bg-white hover:text-primary";
            return (
              <div key={i} className={`relative rounded-2xl overflow-hidden ${bgClass}`} style={b.backgroundUrl ? { backgroundImage: `url(${b.backgroundUrl})`, backgroundSize: "cover", backgroundPosition: "center" } : undefined}>
                {b.backgroundUrl && <div className="absolute inset-0 bg-slate-900/60" />}
                <div className="relative z-10 px-6 md:px-12 py-12 md:py-16 text-center">
                  {b.title && <h2 className="text-3xl md:text-4xl font-bold mb-3">{b.title}</h2>}
                  {b.subtitle && <p className={`text-base md:text-lg mb-7 max-w-2xl mx-auto ${b.backgroundUrl ? "text-white/90" : subColor}`}>{b.subtitle}</p>}
                  <div className="flex flex-wrap gap-3 justify-center">
                    {b.primaryLabel && b.primaryHref && <a href={safeHref(b.primaryHref)} className={`inline-block px-7 py-3 rounded-xl font-semibold transition ${primaryBtn}`}>{b.primaryLabel}</a>}
                    {b.secondaryLabel && b.secondaryHref && <a href={safeHref(b.secondaryHref)} className={`inline-block px-7 py-3 rounded-xl font-semibold transition ${secondaryBtn}`}>{b.secondaryLabel}</a>}
                  </div>
                </div>
              </div>
            );
          }
          case "stats": {
            const items: any[] = b.items || [];
            const cols = Math.min(items.length || 4, 4);
            return (
              <div key={i} className="bg-white rounded-2xl border border-slate-200 p-6 grid gap-6" style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}>
                {items.map((it, j) => (
                  <div key={j} className="text-center">
                    <div className="text-3xl md:text-4xl font-bold text-primary leading-tight">{it.value}</div>
                    <div className="text-xs md:text-sm text-slate-500 mt-1 uppercase tracking-wide">{it.label}</div>
                  </div>
                ))}
              </div>
            );
          }
          case "featureGrid": {
            const items: any[] = b.items || [];
            const cols = Math.max(2, Math.min(4, Number(b.columns) || 3));
            return (
              <div key={i} className="grid gap-5" style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}>
                {items.map((it, j) => (
                  <div key={j} className="bg-white rounded-2xl border border-slate-200 p-6 hover:border-primary/40 hover:shadow-sm transition">
                    {it.icon && <div className="w-12 h-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center text-2xl mb-3">{it.icon}</div>}
                    {it.title && <h3 className="font-bold text-slate-900 mb-1.5">{it.title}</h3>}
                    {it.description && <p className="text-sm text-slate-600 leading-relaxed">{it.description}</p>}
                  </div>
                ))}
              </div>
            );
          }
          case "testimonial": {
            const r = Math.max(0, Math.min(5, Number(b.rating ?? 5)));
            return (
              <div key={i} className="bg-white rounded-2xl border border-slate-200 p-6">
                <div className="flex text-amber-400 text-lg mb-3" aria-label={`${r} out of 5 stars`}>{"★".repeat(r)}<span className="text-slate-200">{"★".repeat(5 - r)}</span></div>
                <p className="text-slate-700 italic mb-4 leading-relaxed">"{b.text}"</p>
                <div className="flex items-center gap-3">
                  {b.avatarUrl
                    ? <img src={safeHref(b.avatarUrl)} alt={b.author || ""} className="w-10 h-10 rounded-full object-cover" />
                    : <div className="w-10 h-10 rounded-full bg-primary/10 text-primary flex items-center justify-center font-semibold">{(b.author || "?").slice(0, 1).toUpperCase()}</div>}
                  <div>
                    {b.author && <div className="font-semibold text-slate-900 text-sm">{b.author}</div>}
                    {b.role && <div className="text-xs text-slate-500">{b.role}</div>}
                  </div>
                </div>
              </div>
            );
          }
          default:
            return null;
        }
      })}
    </div>
  );
}
